import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { AvatarImage } from './avatar-image'
import { useResolvedPresence } from '../stores/presence-store'
import { useVoiceCall } from '../contexts/voice-call-context'
import { useAuth } from '../contexts/auth-context'
import { useRemoteCallPeer } from '../hooks/use-remote-call-peer'
import { useVoiceCallSession } from '../hooks/use-voice-call-session'
import { TransferSheet } from '../features/calls/components/transfer-sheet'
import { TransferEligibilityDialog } from '../features/calls/components/transfer-eligibility-dialog'
import { AddPeoplePickerSheet } from '../features/calls/components/add-people-picker-sheet'
import { TransferInProgressBar } from '../features/calls/components/transfer-in-progress-bar'
import { useCallTransfer } from '../features/calls/hooks/use-call-transfer'
import { useInCallChat } from '../features/group-call/hooks/use-in-call-chat'
import { useInCallChatAlert } from '../features/group-call/hooks/use-in-call-chat-alert'
import { useCallConnectStatus } from '../features/calls/hooks/use-call-connect-status'
import { useEndedCallRecallReady } from '../hooks/use-ended-call-recall-ready'
import { CallReconnectingBanner } from '../features/calls/components/call-reconnecting-banner'
import { CallChatDrawer } from '../features/calls/components/call-chat-drawer'
import { VideoUpgradeUnavailableDialog } from '../features/calls/components/video-upgrade-unavailable-dialog'
import { resolveSelfUserId } from '../services/chat-service'
import { patchVoiceCallSession } from '../stores/voice-call-store'
import { shareLocationWithPeer } from '../services/call-location-share-service'
import { getCurrentPositionShared, GeolocationShareError } from '../utils/geolocation'
import { isDesktopWeb, isNativeApp } from '../utils/platform'
import { CallAudioRouteControl } from '../features/calls/components/call-audio-route-control'
import { showsRecordingIndicator } from '../features/calls/utils/consent-window-end'
import { minimizedCallOverlayClassName } from '../utils/call-overlay-layers'
import {
  canOpenAppSettings,
  openPermissionSettings,
} from '../services/permissions/permissions-service'
import { isCallPermissionSettingsMessage } from '../utils/request-call-permissions'
import { openNativeAppSettings } from '../services/voice-call/native-audio-route'
import { SessionCallContextChip } from '../features/sessions/components/session-call-context-chip'
import { allowsExtraParties } from '../features/calls/utils/session-call-controls'

/** Shown (with an Open Settings button) when the OS denied location for a share. */
const LOCATION_OFF_TEXT = 'Location access is off — enable it for Ascend in Settings.'

function MinimizeChevronIcon() {
  return (
    <svg
      className="h-6 w-6"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

/**
 * Shared "minimize the call overlay" control. The overlay stays mounted (media
 * elements keep playing) — it is only hidden, and the app-shell
 * ReturnToCallBanner takes over as the way back into the call.
 */
export function MinimizeOverlayButton() {
  return (
    <button
      type="button"
      onClick={() => patchVoiceCallSession({ userMinimized: true })}
      className="absolute left-3 top-[max(0.75rem,var(--safe-top))] z-[64] flex h-10 w-10 items-center justify-center rounded-full bg-black/30 text-white transition hover:bg-black/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      aria-label="Minimize call and return to the app"
      title="Minimize call"
    >
      <MinimizeChevronIcon />
    </button>
  )
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.56.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.56 1 1 0 01-.25 1.01l-2.2 2.22z" />
    </svg>
  )
}

function MicIcon({ muted }: { muted: boolean }) {
  if (muted) {
    return (
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M1 1l22 22M9 9v3a3 3 0 005.12 2.12M15 9.34V5a3 3 0 00-5.94-.6" />
        <path d="M12 19v3M8 22h8M12 15a3 3 0 01-3-3V5a3 3 0 016 0v7" />
      </svg>
    )
  }
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 1a3 3 0 00-3 3v7a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v1a7 7 0 01-14 0v-1M12 19v3M8 22h8" />
    </svg>
  )
}

function PersonAddIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M19 8v6M22 11h-6" />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
    </svg>
  )
}

function TransferIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
    </svg>
  )
}

function VideoIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  )
}

function LocationIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

function ScreenShareIcon({ active }: { active: boolean }) {
  if (active) {
    return (
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M1 1l22 22" />
        <path d="M3 5.5A1.5 1.5 0 014.5 4H19M21 7v8a2 2 0 01-2 2H9M3 8v7a2 2 0 002 2h2M8 21h8M12 17v4" />
      </svg>
    )
  }
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4M9 10.5l3-2.5 3 2.5M12 8v5" />
    </svg>
  )
}

function RecordIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
    </svg>
  )
}

function HoldIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  )
}

/**
 * Spaced, labelled call control — round icon button with a text label beneath,
 * matching the phone-dialer reference layout. Larger touch target + visible focus.
 */
function CallControlButton({
  label,
  ariaLabel,
  onClick,
  disabled,
  active,
  pressed,
  title,
  badge,
  tone = 'default',
  children,
}: {
  label: string
  ariaLabel?: string
  onClick: () => void
  disabled?: boolean
  active?: boolean
  pressed?: boolean
  title?: string
  badge?: boolean
  tone?: 'default' | 'danger' | 'accept'
  children: ReactNode
}) {
  const toneClass =
    tone === 'danger'
      ? 'bg-red-500 text-white hover:bg-red-600'
      : tone === 'accept'
        ? 'bg-emerald-500 text-white hover:bg-emerald-600'
        : active
          ? 'bg-emerald-400 text-emerald-950 ring-2 ring-emerald-200/80 hover:bg-emerald-300'
          : 'bg-white/10 text-white hover:bg-white/20'
  return (
    <div className="flex w-20 flex-col items-center gap-2">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={title}
        aria-label={ariaLabel ?? label}
        aria-pressed={pressed}
        className={`relative flex h-16 w-16 items-center justify-center rounded-full shadow-lg transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-50 ${toneClass}`}
      >
        {children}
        {badge && (
          <span
            className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full bg-red-500 ring-2 ring-brand-700"
            aria-hidden
          />
        )}
      </button>
      <span className="text-center text-xs font-medium leading-tight text-white/85">{label}</span>
    </div>
  )
}

function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export function VoiceCallOverlay() {
  const session = useVoiceCallSession()
  const { user } = useAuth()
  const currentUserId = resolveSelfUserId(user?.userId)
  const remotePeer = useRemoteCallPeer()
  const peerPresence = useResolvedPresence(remotePeer)
  const connectStatus = useCallConnectStatus(session)
  const showRecall = useEndedCallRecallReady(session)
  const {
    sheetOpen,
    setSheetOpen,
    warnOpen,
    eligibility,
    pendingTarget,
    bookingCallback,
    onTargetSelected,
    onWarnChoice,
  } = useCallTransfer(currentUserId)
  const {
    requestAcceptCall,
    declineCall,
    hangUp,
    recallLastCall,
    acceptVideoRequest,
    declineVideoRequest,
    addPeople,
    startScreenShare,
    stopScreenShare,
    toggleMute,
    notifyPeerHold,
    setRemoteAudioElement,
    setContentVideoElement,
    refreshContentVideoBinding,
  } = useVoiceCall()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const contentVideoRef = useRef<HTMLVideoElement | null>(null)
  const [now, setNow] = useState(Date.now())
  const [showAddPeople, setShowAddPeople] = useState(false)
  const [showChat, setShowChat] = useState(false)
  const [showVideoUpgradeNotice, setShowVideoUpgradeNotice] = useState(false)
  const [locationShareState, setLocationShareState] = useState<
    'idle' | 'sharing' | 'shared' | 'error'
  >('idle')
  const [locationErrorText, setLocationErrorText] = useState<string | null>(null)
  const [onHold, setOnHold] = useState(false)
  const heldMutedByUsRef = useRef(false)
  const [showRecordingNote, setShowRecordingNote] = useState(false)
  // In-call chat: delivered live to participants AND persisted into the
  // originating conversation thread (when the call has one) like a normal message.
  const chat = useInCallChat(session?.callId, currentUserId, session?.conversationId, {
    presenting: Boolean(
      session && session.mediaType !== 'video' && (!session.isGroup || session.transferRoom),
    ),
    joined: session?.phase === 'active',
    isGroup: false,
    panelOpen: showChat,
  })
  const chatAlert = useInCallChatAlert(chat.messages, currentUserId, showChat)
  // Chat visibility is per-call: the overlay stays mounted between calls, so
  // without this a new call inherits the previous call's open chat drawer
  // (2026-10-07 field bug: the next call "automatically opened the chat").
  const chatResetCallId = session?.callId ?? null
  useEffect(() => {
    setShowChat(false)
  }, [chatResetCallId])
  const resolveChatName = useCallback(
    (senderId: string) => {
      if (senderId === currentUserId) return 'You'
      return remotePeer?.displayName ?? session?.peer.displayName ?? 'Participant'
    },
    [currentUserId, remotePeer, session?.peer.displayName],
  )
  // A transferRoom is a multi-party room under the hood but renders as a 1:1
  // call, so this overlay (not the group overlay) owns its audio element.
  const isActiveVoiceCall = Boolean(
    session && session.mediaType !== 'video' && (!session.isGroup || session.transferRoom),
  )
  const canScreenShare = isDesktopWeb()

  const bindContentVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      contentVideoRef.current = el
      setContentVideoElement(el)
    },
    [setContentVideoElement],
  )

  const bindAudioRef = useCallback(
    (el: HTMLAudioElement | null) => {
      audioRef.current = el
      if (isActiveVoiceCall) {
        setRemoteAudioElement(el)
      }
    },
    [isActiveVoiceCall, setRemoteAudioElement],
  )

  // Sync after DOM commit; only clear when the call ends (callId change / unmount).
  // Do not depend on phase — clearing refs on phase transitions races Chime join on web.
  useLayoutEffect(() => {
    if (!isActiveVoiceCall) return
    setRemoteAudioElement(audioRef.current)
    return () => setRemoteAudioElement(null)
  }, [isActiveVoiceCall, setRemoteAudioElement, session?.callId])

  // Content tiles often bind while the <video> is collapsed (0×0). Re-bind once the
  // presenting layout is visible so mobile WebViews receive frames.
  useLayoutEffect(() => {
    if (!isActiveVoiceCall || !session?.remotePresenting) return
    refreshContentVideoBinding()
  }, [isActiveVoiceCall, refreshContentVideoBinding, session?.callId, session?.remotePresenting])

  useEffect(() => {
    if (session?.phase !== 'active' || !session.connectedAt) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [session?.phase, session?.connectedAt])

  // Clear local hold (and un-mute our playback) whenever the call leaves active.
  useEffect(() => {
    if (session?.phase !== 'active' && onHold) {
      setOnHold(false)
      heldMutedByUsRef.current = false
      if (audioRef.current) audioRef.current.muted = false
    }
  }, [session?.phase, onHold])

  if (!session || session.mediaType === 'video' || (session.isGroup && !session.transferRoom))
    return null

  // Soft presence — another device owns media; banner handles Switch / Dismiss.
  if (session.phase === 'elsewhere') return null

  // iOS: CallKit is presenting this incoming call — it is the one canonical
  // ring surface, so the in-app ring overlay stands down. The overlay appears
  // as soon as the user answers (osRingActive clears / phase leaves incoming).
  if (session.phase === 'incoming' && session.osRingActive) return null

  const voiceSession = session
  const displayPeer = remotePeer ?? session.peer
  const remoteDisplayName = displayPeer.displayName

  const duration =
    voiceSession.phase === 'active' && voiceSession.connectedAt
      ? formatDuration(now - voiceSession.connectedAt)
      : null

  const status = connectStatus.label
  const showControls =
    voiceSession.phase === 'incoming' ||
    voiceSession.phase === 'active' ||
    voiceSession.phase === 'connecting' ||
    voiceSession.phase === 'outgoing'

  const isActive = voiceSession.phase === 'active'
  // Caller-side ringing / connecting (pre-connect): dedicated layout with the
  // End button separated at the bottom of the screen for easy thumb reach.
  const isPreConnect =
    voiceSession.phase === 'outgoing' || voiceSession.phase === 'connecting'
  // Minimized: keep the overlay MOUNTED so the remote <audio> element (and any
  // bound tiles) keep playing, but hide it — the ReturnToCallBanner is the way
  // back. Never hide an incoming ring (the user must answer or decline it).
  // Keyed on the EXPLICIT user-minimize flag ONLY (not background/app-switch) so a
  // missed foreground event can't strand the call UI hidden (blank-screen bug).
  const minimized = Boolean(voiceSession.userMinimized) && voiceSession.phase !== 'incoming'
  const canMinimize =
    isActive || isPreConnect || voiceSession.phase === 'outgoing'
  const isPresenting = Boolean(voiceSession.isPresenting)
  const remotePresenting = Boolean(voiceSession.remotePresenting)
  const videoRequest = voiceSession.videoRequest
  const showScreenShareButton = canScreenShare && isActive

  const toggleScreenShare = () => {
    if (isPresenting) void stopScreenShare()
    else void startScreenShare()
  }

  // 1:1 only — share the caller's live location with the peer mid-call. The
  // entry lands in BOTH parties' direct thread / activity record.
  const canShareLocation = isActive && !voiceSession.isGroup
  const handleShareLocation = async () => {
    if (!currentUserId || locationShareState === 'sharing') return
    setLocationShareState('sharing')
    setLocationErrorText(null)
    try {
      const pos = await getCurrentPositionShared()
      await shareLocationWithPeer({
        selfUserId: currentUserId,
        selfName: user?.displayName ?? 'You',
        peerUserId: displayPeer.userId,
        peerName: remoteDisplayName,
        conversationId: voiceSession.conversationId,
        location: {
          lat: pos.latitude,
          lng: pos.longitude,
          accuracy: pos.accuracy,
        },
      })
      setLocationShareState('shared')
    } catch (err) {
      setLocationShareState('error')
      if (err instanceof GeolocationShareError && err.reason === 'denied') {
        setLocationErrorText(LOCATION_OFF_TEXT)
      } else {
        setLocationErrorText("Couldn't share location — please try again")
      }
    }
  }

  // Local hold: silence both directions (mic + our playback) without dropping the
  // Chime media session, and tell the peer so their device plays hold music.
  // Restores the prior mute state when resumed.
  const handleToggleHold = () => {
    if (!onHold) {
      if (!voiceSession.isMuted) {
        toggleMute()
        heldMutedByUsRef.current = true
      }
      if (audioRef.current) audioRef.current.muted = true
      setOnHold(true)
      notifyPeerHold(true)
    } else {
      if (heldMutedByUsRef.current) {
        toggleMute()
        heldMutedByUsRef.current = false
      }
      if (audioRef.current) audioRef.current.muted = false
      setOnHold(false)
      notifyPeerHold(false)
    }
  }

  return (
    <div
      className={`fixed inset-0 z-[60] flex flex-col items-center justify-center bg-gradient-to-b from-brand-700 to-brand-600 native-modal-top-android ${minimizedCallOverlayClassName(
        minimized,
      )}`}
      data-dark-surface={minimized ? undefined : 'true'}
      role="dialog"
      aria-modal={!minimized}
      aria-hidden={minimized}
      aria-label="Voice call"
    >
      <audio ref={bindAudioRef} data-call-remote="true" className="sr-only" aria-hidden playsInline />

      {canMinimize && !minimized && <MinimizeOverlayButton />}

      <CallReconnectingBanner show={connectStatus.isReconnecting} />

      {voiceSession.notice && (
        <div
          className="pointer-events-none absolute inset-x-0 top-[max(1rem,var(--safe-top))] z-[62] flex justify-center px-4"
          role="status"
          aria-live="polite"
        >
          <span className="rounded-full bg-slate-900/85 px-4 py-2 text-sm font-medium text-white shadow-lg">
            {voiceSession.notice}
          </span>
        </div>
      )}

      {chatAlert.banner && !showChat && (
        <button
          type="button"
          onClick={() => setShowChat(true)}
          className="absolute inset-x-0 top-[max(3.5rem,calc(var(--safe-top)+2.5rem))] z-[63] mx-auto flex max-w-sm items-start gap-2 rounded-2xl bg-slate-900/90 px-4 py-3 text-left text-white shadow-xl transition hover:bg-slate-900"
          role="status"
          aria-live="polite"
        >
          <span className="mt-0.5 shrink-0 text-emerald-300">
            <ChatIcon />
          </span>
          <span className="min-w-0">
            <span className="block text-xs font-semibold text-emerald-200">
              {resolveChatName(chatAlert.banner.senderId)} · in-call message
            </span>
            <span className="block truncate text-sm text-white/90">{chatAlert.banner.text}</span>
          </span>
        </button>
      )}

      {voiceSession.phase === 'active' && duration && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-[61] flex items-center justify-center bg-gradient-to-b from-black/70 to-transparent px-4 pb-6 pt-[max(0.75rem,var(--safe-top))]"
          aria-live="polite"
        >
          <span className="rounded-full bg-black/60 px-4 py-1.5 text-base font-semibold tabular-nums tracking-wide text-white">
            {duration}
          </span>
        </div>
      )}

      <div
        className={`animate-fade-slide-in flex h-full w-full flex-col overflow-y-auto text-white ${
          remotePresenting ? 'max-w-3xl' : ''
        }`}
      >
        <div
          className={`flex min-h-full flex-1 flex-col items-center px-6 pb-[max(1.5rem,var(--safe-bottom))] pt-[max(2rem,var(--safe-top))] ${
            isPreConnect ? '' : 'justify-center'
          }`}
        >
          {/* Ringing layout: spacer keeps the avatar/name block in the upper-middle
              zone while the End button sits at the very bottom. */}
          {isPreConnect && <div className="min-h-6 flex-1" aria-hidden />}
          {/* The peer's shared screen (content tile) is bound to this element;
              kept mounted with real dimensions so Chime can bind before visible. */}
          <div
            className={
              remotePresenting
                ? 'mb-4 w-full'
                : 'pointer-events-none fixed left-0 top-0 h-px w-px overflow-hidden opacity-0'
            }
            aria-hidden={!remotePresenting}
          >
            <video
              ref={bindContentVideoRef}
              className="aspect-video w-full rounded-xl border border-white/15 bg-black object-contain"
              autoPlay
              playsInline
              muted
              aria-label={`${remoteDisplayName} is presenting`}
            />
          </div>

          {!remotePresenting && (
            <div
              className={`mb-4 ${voiceSession.phase === 'incoming' || voiceSession.phase === 'outgoing' ? 'animate-pulse' : ''}`}
            >
              <AvatarImage
                name={remoteDisplayName}
                avatarUrl={displayPeer.avatarUrl}
                size={88}
                variant="provider"
                className="ring-4 ring-white/20"
                presence={peerPresence}
                fallback="logo"
              />
            </div>
          )}

          {voiceSession.callPurpose && voiceSession.callPurpose !== 'normal' && (
            <span
              className={`mb-2 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide shadow-sm ${
                voiceSession.callPurpose === 'urgent'
                  ? 'bg-red-500 text-white'
                  : 'bg-amber-400 text-amber-950'
              }`}
              role="status"
            >
              <span aria-hidden>{voiceSession.callPurpose === 'urgent' ? '🚨' : '🟠'}</span>
              {voiceSession.callPurpose === 'urgent' ? 'Urgent help call' : 'On-call'}
            </span>
          )}
          <SessionCallContextChip sessionUuid={voiceSession.sessionUuid} className="mb-2" />
          <h2 className="text-center text-xl font-bold">{remoteDisplayName}</h2>
          <p className="mt-1 text-center text-sm text-white/80" aria-live="polite">
            {remotePresenting
              ? `${remoteDisplayName} is presenting`
              : onHold
                ? 'On hold'
                : voiceSession.peerHold
                  ? `${remoteDisplayName} placed you on hold`
                  : status}
          </p>
          {isPresenting && (
            <p className="mt-1 text-center text-xs font-semibold text-emerald-200" role="status">
              You are sharing your screen
            </p>
          )}
          {locationShareState === 'shared' && (
            <p className="mt-1 text-center text-xs font-semibold text-emerald-200" role="status">
              Location shared with {remoteDisplayName}
            </p>
          )}
          {locationShareState === 'error' && (
            <div className="mt-1 flex max-w-xs flex-col items-center gap-1.5" role="alert">
              <p className="text-center text-xs font-semibold text-amber-200">
                {locationErrorText ?? "Couldn't share location — please try again"}
              </p>
              {locationErrorText === LOCATION_OFF_TEXT && canOpenAppSettings() && (
                <button
                  type="button"
                  onClick={() => void openPermissionSettings()}
                  className="min-h-9 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold text-white hover:bg-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  Open Settings
                </button>
              )}
            </div>
          )}
          {showRecordingNote && showsRecordingIndicator(voiceSession) && (
            <p className="mt-1 max-w-xs text-center text-xs font-semibold text-white/80" role="status">
              This call is recorded to generate your summary &amp; transcript.
            </p>
          )}
          {videoRequest === 'outgoing' && (
            <p className="mt-2 text-center text-sm text-white/90" aria-live="polite">
              Waiting for {remoteDisplayName} to accept video…
            </p>
          )}
          {voiceSession.errorMessage && voiceSession.phase !== 'ended' && (
            <p className="mt-2 max-w-xs text-center text-xs text-amber-200" role="status">
              {voiceSession.errorMessage}
            </p>
          )}
          {isNativeApp() && isCallPermissionSettingsMessage(voiceSession.errorMessage) && (
            <button
              type="button"
              onClick={() => void openNativeAppSettings()}
              className="mt-3 rounded-lg bg-white/15 px-4 py-2 text-xs font-semibold text-white transition hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              Open app settings
            </button>
          )}

          {videoRequest === 'incoming' && (
            <div className="mt-5 w-full rounded-2xl bg-white/10 px-4 py-4 text-center">
              <p className="text-sm font-medium text-white" aria-live="polite">
                {remoteDisplayName} would like to switch to video
              </p>
              <div className="mt-4 flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={() => declineVideoRequest()}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition hover:bg-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  aria-label="Decline switch to video"
                >
                  <PhoneIcon className="h-5 w-5 rotate-[135deg]" />
                </button>
                <button
                  type="button"
                  onClick={() => void acceptVideoRequest()}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg transition hover:bg-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  aria-label="Accept switch to video"
                >
                  <VideoIcon />
                </button>
              </div>
            </div>
          )}

          <TransferInProgressBar transfer={voiceSession.transfer} />

          {showControls &&
            (voiceSession.phase === 'incoming' ? (
              <div className="mt-8 flex w-full flex-col items-center gap-8">
                {/* Passive indicator: only visible when the ring/answer audio
                    will land somewhere non-obvious (earbuds / car). */}
                <CallAudioRouteControl active variant="pill" />
                <div className="flex w-full items-center justify-center gap-14">
                <CallControlButton
                  label="Decline"
                  ariaLabel="Decline call"
                  onClick={() => void declineCall()}
                  tone="danger"
                >
                  <PhoneIcon className="h-7 w-7 rotate-[135deg]" />
                </CallControlButton>
                <CallControlButton
                  label="Accept"
                  ariaLabel="Accept call"
                  onClick={() => requestAcceptCall()}
                  tone="accept"
                >
                  <PhoneIcon className="h-7 w-7" />
                </CallControlButton>
                </div>
              </div>
            ) : isPreConnect ? (
              <>
                {/* Middle zone: Mute + Speaker as a spaced pair */}
                <div className="mt-12 flex w-full items-center justify-center gap-16">
                  <CallControlButton
                    label={voiceSession.isMuted ? 'Unmute' : 'Mute'}
                    ariaLabel={voiceSession.isMuted ? 'Unmute microphone' : 'Mute microphone'}
                    onClick={() => toggleMute()}
                    active={voiceSession.isMuted}
                    pressed={voiceSession.isMuted}
                  >
                    <MicIcon muted={voiceSession.isMuted} />
                  </CallControlButton>
                  <CallAudioRouteControl active />
                </div>
                {/* Clear gap, then End alone at the bottom (thumb reach) */}
                <div className="min-h-10 flex-[1.5]" aria-hidden />
                <CallControlButton
                  label="End"
                  ariaLabel="End call"
                  onClick={() => void hangUp()}
                  tone="danger"
                >
                  <PhoneIcon className="h-7 w-7 rotate-[135deg]" />
                </CallControlButton>
              </>
            ) : (
              <div className="mt-8 flex w-full flex-col items-center gap-8">
                <div className="grid grid-cols-3 justify-items-center gap-x-6 gap-y-5">
                  {(voiceSession.phase === 'active' ||
                    voiceSession.phase === 'connecting' ||
                    voiceSession.phase === 'outgoing') && (
                    <>
                      <CallControlButton
                        label={voiceSession.isMuted ? 'Unmute' : 'Mute'}
                        ariaLabel={voiceSession.isMuted ? 'Unmute microphone' : 'Mute microphone'}
                        onClick={() => toggleMute()}
                        active={voiceSession.isMuted}
                        pressed={voiceSession.isMuted}
                      >
                        <MicIcon muted={voiceSession.isMuted} />
                      </CallControlButton>

                      <CallAudioRouteControl active />

                      {isActive && (
                        <CallControlButton
                          label="Video"
                          ariaLabel="Switch to video call"
                          onClick={() => setShowVideoUpgradeNotice(true)}
                          title="Switch to video"
                        >
                          <VideoIcon />
                        </CallControlButton>
                      )}

                      {isActive && (
                        <CallControlButton
                          label="Chat"
                          ariaLabel={chatAlert.hasUnread ? 'In-call chat — new message' : 'In-call chat'}
                          onClick={() => setShowChat((v) => !v)}
                          active={showChat}
                          pressed={showChat}
                          title="In-call chat"
                          badge={chatAlert.hasUnread && !showChat}
                        >
                          <ChatIcon />
                        </CallControlButton>
                      )}

                      {canShareLocation && (
                        <CallControlButton
                          label="Location"
                          ariaLabel={
                            locationShareState === 'shared'
                              ? `Location shared with ${remoteDisplayName}`
                              : 'Share your location'
                          }
                          onClick={() => void handleShareLocation()}
                          disabled={locationShareState === 'sharing'}
                          active={locationShareState === 'shared'}
                          pressed={locationShareState === 'shared'}
                          title="Share your location"
                        >
                          <LocationIcon />
                        </CallControlButton>
                      )}

                      {isActive && (
                        <CallControlButton
                          label="Hold"
                          ariaLabel={onHold ? 'Resume call' : 'Hold call'}
                          onClick={handleToggleHold}
                          active={onHold}
                          pressed={onHold}
                          title={onHold ? 'Resume' : 'Hold'}
                        >
                          <HoldIcon />
                        </CallControlButton>
                      )}

                      {isActive && showsRecordingIndicator(voiceSession) && (
                        <CallControlButton
                          label="Record"
                          ariaLabel="Recording on — tap for details"
                          onClick={() => setShowRecordingNote((v) => !v)}
                          active
                          pressed
                          title="This call is being recorded"
                        >
                          <RecordIcon />
                        </CallControlButton>
                      )}

                      {showScreenShareButton && (
                        <CallControlButton
                          label="Share"
                          ariaLabel={isPresenting ? 'Stop sharing your screen' : 'Share your screen'}
                          onClick={toggleScreenShare}
                          active={isPresenting}
                          pressed={isPresenting}
                          title={isPresenting ? 'Stop sharing your screen' : 'Share your screen'}
                        >
                          <ScreenShareIcon active={isPresenting} />
                        </CallControlButton>
                      )}

                      {/* A telehealth session call is exactly provider + member:
                          no transfer, no extra participants. */}
                      {isActive && allowsExtraParties(voiceSession) && (
                        <CallControlButton
                          label="Transfer"
                          ariaLabel="Transfer call"
                          onClick={() => setSheetOpen(true)}
                          title="Transfer call"
                        >
                          <TransferIcon />
                        </CallControlButton>
                      )}

                      {isActive && allowsExtraParties(voiceSession) && (
                        <CallControlButton
                          label="Add"
                          ariaLabel="Add person to call"
                          onClick={() => setShowAddPeople(true)}
                          title="Add person to call"
                        >
                          <PersonAddIcon />
                        </CallControlButton>
                      )}
                    </>
                  )}
                </div>

                <CallControlButton label="End" ariaLabel="End call" onClick={() => void hangUp()} tone="danger">
                  <PhoneIcon className="h-7 w-7 rotate-[135deg]" />
                </CallControlButton>
              </div>
            ))}

          {showRecall && (
            <button
              type="button"
              onClick={() => void recallLastCall()}
              className="mt-6 w-full rounded-xl bg-white/15 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              Try again
            </button>
          )}
        </div>
      </div>

      <TransferSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onConfirm={onTargetSelected}
        excludeUserIds={
          session
            ? [session.callerId === currentUserId ? session.calleeId : session.callerId]
            : []
        }
      />
      <TransferEligibilityDialog
        open={warnOpen}
        targetName={pendingTarget?.displayName ?? 'this user'}
        result={eligibility}
        bookingCallback={bookingCallback}
        onChoose={(choice) => void onWarnChoice(choice)}
      />
      <AddPeoplePickerSheet
        open={showAddPeople}
        onClose={() => setShowAddPeople(false)}
        onAdd={(users) => void addPeople(users)}
        excludeUserIds={
          session
            ? [session.callerId, session.calleeId, currentUserId ?? ''].filter(Boolean)
            : []
        }
      />
      <CallChatDrawer
        open={showChat && voiceSession.phase === 'active'}
        onClose={() => setShowChat(false)}
        chat={chat}
        resolveName={resolveChatName}
        placeholder={`Message ${remoteDisplayName}…`}
      />
      <VideoUpgradeUnavailableDialog
        open={showVideoUpgradeNotice}
        onClose={() => setShowVideoUpgradeNotice(false)}
      />
    </div>
  )
}
